import type { FC, RefObject } from 'react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  KeyboardArrowDownIcon as ArrowDownIcon,
  useTheme,
} from '@/shared/ui/mui';

interface DesktopLandingNavProps {
  isHomePage: boolean;
  isProviderView: boolean;
  isScrolled: boolean;
  navLinkColor: string;
  featuresAnchorRef: RefObject<HTMLButtonElement>;
  isFeaturesMenuOpen: boolean;
  onFeaturesToggle: () => void;
  onScrollToAnchor: (anchor: string) => void;
}

export const DesktopLandingNav: FC<DesktopLandingNavProps> = ({
  isHomePage,
  isProviderView,
  isScrolled,
  navLinkColor,
  featuresAnchorRef,
  isFeaturesMenuOpen,
  onFeaturesToggle,
  onScrollToAnchor,
}) => {
  const theme = useTheme();

  const linkHoverColor = isScrolled ? theme.palette.primary.main : theme.palette.common.white;
  const linkSx = {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: navLinkColor,
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    '&:hover': { color: linkHoverColor },
  };

  return (
    <Box
      component="nav"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        ml: 'auto',
        mr: 4,
      }}
    >
      {isHomePage && (
        <Box
          component="a"
          href="#how-it-works"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            onScrollToAnchor('#how-it-works');
          }}
          sx={{ ...linkSx, whiteSpace: 'nowrap' }}
        >
          How It Works
        </Box>
      )}

      {isProviderView && (
        <Button
          ref={featuresAnchorRef}
          onClick={onFeaturesToggle}
          endIcon={
            <ArrowDownIcon
              sx={{
                fontSize: 18,
                transform: isFeaturesMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          }
          sx={{
            color: navLinkColor,
            textTransform: 'none',
            px: 0,
            minWidth: 0,
            fontWeight: 500,
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: 'transparent',
              color: linkHoverColor,
            },
          }}
        >
          Features
        </Button>
      )}

      {isProviderView && isHomePage && (
        <Box
          component="a"
          href="#pricing"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            onScrollToAnchor('#pricing');
          }}
          sx={linkSx}
        >
          Pricing
        </Box>
      )}

      <Box component={RouterLink} to="/contact" sx={linkSx}>
        Support
      </Box>
    </Box>
  );
};
